import React, {useEffect, useState} from 'react'
import {Input} from '@thryvlabs/maverick'
import {Button} from '../../core/shared/Button'
import Modal from '../../core/shared/Modal'
import {UserPlusIcon} from './action-icons'
import {useForm} from 'react-hook-form'
import * as yup from 'yup'
import {yupResolver} from '@hookform/resolvers/yup'
import {toast} from 'react-toastify'
import {useAsync} from '../../utils/use-async'
import {useAuthClient} from '../../utils/use-auth-client'

// Same form/behaviour as CreateNewKeapPartnerModal in components/side-navbar.js
// (kept separate so the existing sidebar stays untouched). Gated by the
// Keap_Account_Management feature flag in the caller.

const KEAP_PARTNER_ROLE = 'KeapPartner'

const keapUserformSchema = yup.object().shape({
  email: yup
    .string()
    .email('Email must be a valid email')
    .min(3, 'Email must be at least 3 characters')
    .max(100, 'Email cannot be longer than 100 characters')
    .required(),
  firstName: yup
    .string()
    .min(3, 'First Name must be at least 3 characters')
    .max(100, 'First Name cannot be longer than 100 characters')
    .required(),
  lastName: yup
    .string()
    .min(3, 'Last Name must be at least 3 characters')
    .max(100, 'Last Name cannot be longer than 100 characters')
    .required(),
  sfPartnerAccountId: yup
    .string()
    .min(3, 'SF Partner Account Id must be at least 3 characters')
    .max(100, 'SF Partner Account Id cannot be longer than 100 characters'),
  keapPartnerId: yup
    .string()
    .min(3, 'Keap Partner Id must be at least 3 characters')
    .max(100, 'Keap Partner Id cannot be longer than 100 characters'),
  password: yup
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(100, 'Password cannot be longer than 100 characters')
    .required(),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password'), null], 'Passwords does not match')
    .required(),
})

const FIELDS = [
  {name: 'firstName', label: 'First Name', type: 'text'},
  {name: 'lastName', label: 'Last Name', type: 'text'},
  {name: 'userName', label: 'User Name', ariaLabel: 'user Name', type: 'text'},
  {name: 'email', label: 'Email', type: 'text'},
  {name: 'keapPartnerId', label: 'Keap Partner ID', type: 'text'},
  {
    name: 'sfPartnerAccountId',
    label: 'SF Partner Account Record ID',
    type: 'text',
  },
  {name: 'password', label: 'Password', type: 'password'},
  {name: 'confirmPassword', label: 'Retype Password', type: 'password'},
]

const showCreateUserResult = result => {
  if (result.hasWarningorError) {
    if (result.userCreated && result.error) {
      toast.success('User is created successfully.')
      toast.error(result.errorContext + ' - ' + result.error?.message)
    } else if (result.error) {
      toast.error(result?.error?.message)
    } else {
      toast.error('error in Create User')
    }
  } else {
    toast.success('User is created successfully.')
  }
}

function CreateKeapPartnerModal({isCollapsed}) {
  const [isOpen, setIsOpen] = useState(false)
  const client = useAuthClient()
  const {run, isLoading} = useAsync()
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: {errors},
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(keapUserformSchema),
  })

  const firstName = watch('firstName')
  const lastName = watch('lastName')

  useEffect(() => {
    const userName =
      (firstName ? firstName[0] : '') +
      (lastName ? lastName : '').replace(/\s+/g, '')
    setValue('userName', userName)
  }, [firstName, lastName, setValue])

  const handleCreateUsers = async data => {
    try {
      const response = await run(
        client('user/createkeappartneruser', {
          data: {...data, userRole: KEAP_PARTNER_ROLE},
        }),
      )
      showCreateUserResult(response)
    } catch (error) {
      // FULSAS-7078: the API returns a 4xx (e.g. 409 duplicate email) with the
      // result body on error.data — render it so the specific message shows.
      if (error?.data?.hasWarningorError) {
        showCreateUserResult(error.data)
      } else {
        toast.error(error.message)
      }
    }
  }

  const trigger = (
    <Button
      variant="secondary"
      size="sm"
      aria-label={isCollapsed ? 'Create Keap User' : undefined}
      title={isCollapsed ? 'Create Keap User' : undefined}
      onClick={() => setIsOpen(true)}
    >
      <UserPlusIcon />
      {!isCollapsed && 'Create Keap User'}
    </Button>
  )

  return (
    <>
      {trigger}
      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Create Keap User"
        closeOnBackdrop={false}
        footerAlign="center"
        footer={
          <Button
            variant="primary"
            isLoading={isLoading}
            onClick={handleSubmit(handleCreateUsers)}
          >
            Create
          </Button>
        }
      >
        {FIELDS.map(field => (
          <div className="mt-4 mb-3" key={field.name}>
            <Input
              className="w-100"
              type={field.type}
              placeholder={field.label}
              aria-label={field.ariaLabel || field.label}
              register={register}
              errors={errors}
              name={field.name}
              withLabel
              labelType="floating"
              variant="default"
            />
          </div>
        ))}
      </Modal>
    </>
  )
}

export {CreateKeapPartnerModal}
